<template>
  <v-card elevation="1" class="container-card" data-testid="order-cart">
    <v-row align="center" justify="start" class="top-bar pa-3">
      <v-col cols="12">
        <div class="d-flex justify-start align-center">
          <v-icon class="mr-2" icon="shopping_cart" />
          <div class="md-light">Shopping Cart</div>
          <v-spacer />
          <v-progress-circular
            v-if="loading"
            :indeterminate="true"
            size="20"
            width="2"
            class="loading-wheel"
          />
        </div>
      </v-col>
    </v-row>
    <v-col cols="12">
      <v-divider />
    </v-col>
    <v-row align="center" justify="start" class="pa-3">
      <v-col
        cols="12"
        v-if="!hideComponentInfo"
        :style="mobile ? '' : 'overflow: auto; height: 175px'"
        class="pa-3"
      >
        <div v-for="(item, index) in formattedCart" :key="item + index + 'x'">
          <div class="d-flex justify-start align-center order-item-card" style="flex-wrap: nowrap">
            <div style="width: 50px; min-width: 50px; max-height: 50px">
              <v-img contain :src="getImage(getImageID(item))" class="mr-2" />
            </div>
            <div style="min-width: 0; flex: 1 1 auto; max-width: 200px">
              <div class="mr-2 overflow-ellipsis">{{ item.productName }}</div>
              <div class="d-flex justify-start align-center">
                <div class="md-light mr-2 overflow-ellipsis">
                  {{ item.primaryAttributeValue }}
                </div>
                <div class="md-light mr-2 overflow-ellipsis">
                  {{ item.secondaryAttributeValue }}
                </div>
              </div>
            </div>
            <v-spacer />
            <v-btn
              icon
              variant="plain"
              @click="decreaseQuantity(index)"
              :disabled="item.quantity < 2 || loading || disabled"
              ><v-icon icon="remove" /></v-btn
            >
            <div class="quantity">
              <div class="d-flex justify-center align-center">
                {{ item.quantity }}
              </div>
            </div>
            <v-btn
              icon
              variant="plain"
              @click="increaseQuantity(index)"
              :disabled="loading || disabled"
              ><v-icon icon="add" /></v-btn
            >
            <div v-if="item.price != null" class="md-light">
              ${{ formatPrice(getPrice(item)) }}
            </div>
            <div
              v-else
              class="md-light"
              title="Price unavailable"
              :data-testid="`order-cart-line-price-unavailable-${index}`"
            >
              &mdash;
            </div>
            <v-btn
              icon
              variant="plain"
              @click="deleteOrderItem(index)"
              :disabled="loading || disabled"
              ><v-icon icon="delete" /></v-btn
            >
          </div>
        </div>
      </v-col>
      <v-col cols="12">
        <v-divider />
      </v-col>
      <!--
        Server line prices (cart_prices, 2026-09-29). Totals are withheld
        until every line is priced: a total built from unpriced lines would
        be a number this cart does not have. The order cannot be placed in
        that state either (setCartValid).
      -->
      <v-col
        v-if="linePricesState === 'loading'"
        cols="12"
        class="md-light"
        data-testid="order-cart-prices-loading"
      >
        Loading prices...
      </v-col>
      <v-col
        v-else-if="formattedCart.length && !pricesReady"
        cols="12"
        data-testid="order-cart-prices-error"
      >
        <v-alert type="error" variant="tonal">
          We couldn't load prices for this cart, so the order can't be placed
          right now.
          <div
            v-if="linePricesError"
            class="mt-1"
            data-testid="order-cart-prices-error-detail"
          >{{ linePricesError }}</div>
          <v-btn
            variant="text"
            class="ml-2"
            data-testid="order-cart-prices-retry"
            @click="refreshLinePrices"
            >Retry</v-btn
          >
        </v-alert>
      </v-col>
      <!--
        THE money of this order is the SERVER's (#540, Jake 2026-10-26): the
        lines below are `checkout_quote.totals`, the breakdown the storefront
        cart, the confirmation, the email and the order pages render
        (api/src/pricing/order_totals.py). Nothing is added up here. Until
        the server has priced the cart's current inputs -- ship-to, live
        shipping rate, department, coupon -- only the items figure shows,
        with a line saying what is still needed.
      -->
      <v-row
        v-if="pricesReady"
        justify="start"
        class="pa-3"
        data-testid="order-cart-totals"
      >
        <template v-if="serverTotals">
          <v-col
            v-for="line in serverTotals.lines"
            :key="line.key"
            cols="12"
            class="py-1"
            :data-testid="`order-cart-line-${line.key}`"
          >
            <div class="d-flex">
              <div>{{ line.label }}:</div>
              <v-spacer />
              <div>{{ formatAmount(line.amount) }}</div>
            </div>
          </v-col>
          <v-col cols="12" class="py-1" data-testid="order-cart-total">
            <div class="d-flex md-subheader-no-spacing">
              <div>Total:</div>
              <v-spacer />
              <div>${{ formatPrice(serverTotals.total) }}</div>
            </div>
          </v-col>
        </template>
        <template v-else>
          <v-col cols="12" class="py-1" data-testid="order-cart-line-items">
            <div class="d-flex">
              <div>Items:</div>
              <v-spacer />
              <div>${{ formatPrice(subTotal) }}</div>
            </div>
          </v-col>
          <v-col
            v-if="shippingRateUnavailable"
            cols="12"
            class="py-1 text-error"
            data-testid="order-cart-shipping-unavailable"
          >
            Shipping: Rate unavailable
          </v-col>
          <v-col
            v-else
            cols="12"
            class="py-1 md-light"
            data-testid="order-cart-totals-pending"
          >
            {{ pendingMessage }}
          </v-col>
        </template>
      </v-row>
    </v-row>
  </v-card>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import {
  isUnparseableAddress,
  unparseableAddressMessage,
} from "@/utils/addressParse";
import {
  isNoShipOriginResponse,
  logShippingRateProvider,
  readShippingQuoteId,
  storeRatesLive,
} from "@/services/shippingService";
import store from "@/store";

import helpers from "@/mixins/helpers";
import { useEnableFundraising } from "@/composables/useEnableFundraising";
import { mapGetters } from "vuex";
// PR-N1: replace the legacy variant_key === primary_attribute_value
// boolean predicate (which missed on ~all real orders) with a call
// to the PR-J variant resolver via the new batch endpoint. The
// shared helpers + composable live in @/utils/decorationLabels +
// @/composables/useResolveBatch so all 5 shopper-flow files share
// one implementation.
import {
  buildCartDecorations,
  collectResolverPairs,
} from "@/utils/decorationLabels";
import { useResolveBatch } from "@/composables/useResolveBatch";
import { cartStoreArgs } from "@/router/storefrontStoreId";
import { quoteMemoFor } from "@/components/Storefront/checkoutQuoteMemo";
import {
  isUsableShippingQuote,
  liveShippingQuoteKey,
  liveShippingRequest,
  requestCheckoutQuote,
  shipToQuoteLocation,
} from "@/components/Storefront/checkoutQuoteClient";
import {
  departmentPickerMode,
  storeResolvesDestination,
} from "@/utils/campaign";
import { SUPPRESS_ERROR_TOAST } from "@/interceptors/authResponseInterceptor";
import {
  cartPricesErrorMessage,
  fetchCartLinePrices,
  lineUnitCents,
} from "@/components/Storefront/cartLinePrices";

// How long the cart waits for its inputs to settle (typing an address, a
// quantity step) before it asks the server to price them.
const QUOTE_DEBOUNCE_MS = 300;

// What the totals area says while the server has not priced the cart.
const PENDING_MESSAGES = {
  idle: "Totals appear once the order is priced.",
  "needs-address": "Enter the shipping address to price shipping and tax.",
  "awaiting-department":
    "Choose the department to price shipping and tax.",
  "no-destination":
    "This store has no shipping address to ship to, so shipping cannot be priced.",
  loading: "Pricing this order...",
  failed: "We couldn't price this order. Change a detail to try again.",
};

// Staff can fix a missing origin (a shopper cannot), so Add Order says how.
const NO_SHIP_ORIGIN_STAFF_MESSAGE =
  "This store has no shipping origin address, so shipping cannot be " +
  "quoted. Add the client company's shipping address, or the " +
  "distributor's ship-from address on Company.";

export default {
  components: {},
  mixins: [helpers],

  setup() {
    // Fundraiser / Donations is hidden platform-wide unless an SA turns it
    // on (#623); the store's own switch alone no longer decides.
    const { enableFundraising } = useEnableFundraising();
    return { enableFundraising };
  },

  props: {
    storeID: {
      type: Number,
      required: true,
    },
    campaign: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    // The typed department pick (AddOrder's), posted to checkout_quote as
    // `client_department_id`; null when the store needs none.
    clientDepartmentId: {
      type: Number,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      products: [],
      formattedCart: [],
      hideComponentInfo: false,
      loading: false,
      mobile: window.innerWidth < 960,

      // Bumped by every refreshQuote(); a pass that finishes after a newer
      // one started writes nothing (see refreshQuote).
      quoteSeq: 0,
      quoteTimer: null,
      // The server's `checkout_quote` answer for the cart's CURRENT inputs,
      // or null. Its `totals` is what the totals area renders.
      serverQuote: null,
      // Why there is no `serverQuote` yet: a PENDING_MESSAGES key, or
      // "loaded".
      quoteState: "idle",

      shippingCost: 0,
      // The server's id for the live rate in `shippingCost`, or null (no
      // live rate: flat, unavailable). See setShopperShippingQuoteId.
      shippingQuoteId: null,
      // True when a smart-shipping quote was needed and could NOT be
      // produced (request failed, provider had no rate). It is never
      // rendered as $0: the line says so, the order cannot be placed, and
      // AddOrder posts shipping_rate_available: false (#2882).
      shippingRateUnavailable: false,

      // The items figure shown before the server has priced the order.
      subTotal: 0,

      displayCartLimitError: true,

      // PR-N1: Map keyed by `${decoration_id}|${color_sku}` carrying
      // the resolved variant for each (decoration, color) pair the
      // cart needs. Populated by ``refreshDecorationResolution``
      // whenever the cart shape changes. Empty Map = "resolver not
      // yet populated" -- the cart computed treats that the same as
      // "no label routes" so first-paint stays consistent with the
      // pre-PR-N1 silent-miss behavior.
      decorationResolution: new Map(),
      // The (decoration, colour) pair set last sent to resolve_batch. The
      // resolver is re-run only when this changes -- see
      // refreshDecorationResolution.
      decorationPairsKey: null,

      // Server line prices from the read-only cart_prices RPC: a lookup
      // keyed by line identity, and "idle" | "loading" | "loaded" |
      // "error". There is no client-side price to fall back on.
      linePrices: null,
      linePricesState: "idle",
      // Id of the newest cart_prices request; see refreshLinePrices.
      linePricesSeq: 0,
      // The server's reason when it could not price the cart, or null.
      linePricesError: null,
    };
  },

  computed: {
    // The store's own fundraising switch AND the platform switch (#623).
    fundraisingOn() {
      return !!(this.campaign?.fundraising_enabled && this.enableFundraising);
    },
    ...mapGetters([
      "getShopperLocation",
      "getShopperCoupon",
      "getShopperAllowance",
    ]),

    // The server's breakdown (api/src/pricing/order_totals.py) minus the
    // "subtotal" line, which restates the total; or null.
    serverTotals() {
      const totals = this.serverQuote && this.serverQuote.totals;
      if (!totals || !Array.isArray(totals.lines)) return null;
      return {
        total: totals.total,
        lines: totals.lines.filter((line) => line.key !== "subtotal"),
      };
    },

    pendingMessage() {
      return PENDING_MESSAGES[this.quoteState] || PENDING_MESSAGES.idle;
    },

    // Everything the server's price depends on. Any change re-quotes.
    quoteInputs() {
      const location = this.getShopperLocation;
      return JSON.stringify({
        ready: this.pricesReady,
        cart: this.$store.state.cart,
        location: location && typeof location === "object" ? location : null,
        department: this.clientDepartmentId,
        coupon: this.getShopperCoupon || null,
        allowance: this.getShopperAllowance,
      });
    },

    cart() {
      let cartItems = [];

      // `state.cart` is the cart of THIS store (per-store carts U6); the
      // account itself carries no cart.
      const storeCart = this.$store.state.cart;
      if (
        this.hideComponentInfo ||
        this.products == [] ||
        !this.$store.state.account ||
        !Array.isArray(storeCart)
      ) {
        return cartItems;
      }
      for (let cartIndex in storeCart) {
        let cartItem = storeCart[cartIndex];
        let campaignProduct;
        for (let index = 0; index < this.products.length; index++) {
          campaignProduct = this.products[index];
          if (cartItem.campaign_product_id == campaignProduct.id) {
            break;
          }
        }
        if (!campaignProduct) {
          return cartItems;
        }

        // Priced by the SERVER at the line's real quantity (cart_prices,
        // 2026-09-29). No fallback: this list payload carries no per-size
        // price, so the old helper call landed on default_sale_price -- the
        // MIN size price -- for every line. An unpriced line keeps
        // `price: null` and blocks the order.
        const unitCents = lineUnitCents(this.linePrices, cartItem);
        const price = unitCents === undefined ? null : unitCents / 100;
        const total =
          unitCents === undefined ? null : (unitCents * cartItem.quantity) / 100;

        let image = null;

        if (campaignProduct.images) {
          image = campaignProduct.images[cartItem.primary_attribute_value];
        }
        // PR-N1: decoration label appears IFF the resolver returned
        // hint=ok for the (decoration_id, primary_attribute_value)
        // pair. Pre-PR-N1 this ran an inline variant_key match that
        // missed on ~all real orders. Tested in
        // utils/__tests__/decorationLabels.spec.js (helper) and
        // smoke-checked end-to-end against 8083.
        let decorations = buildCartDecorations(
          campaignProduct,
          cartItem.primary_attribute_value,
          this.decorationResolution
        );
        cartItems.push({
          index: cartIndex,
          campaignProduct: campaignProduct,
          productName: campaignProduct.product_name,
          primaryAttributeValue: cartItem.primary_attribute_value,
          secondaryAttributeValue: cartItem.secondary_attribute_value,
          // Part of the line's identity: two lines of one size can differ
          // only by logo. See updateQuantity.
          selectedArtworkId: cartItem.selected_artwork_id ?? null,
          decorations: decorations,
          image: image,
          sku: campaignProduct.sku,
          price: price,
          quantity: cartItem.quantity,
          total: total,
        });
      }
      return cartItems;
    },

    // Every line carries a server price. Gates the totals and the order.
    pricesReady() {
      return (
        this.linePricesState === "loaded" &&
        this.cart.every(
          (row) => row.price !== null || !(Number(row.quantity) > 0)
        )
      );
    },

    cartTotalWeight() {
      let weight = 0;
      this.formattedCart.forEach((product) => {
        if (product && product.campaignProduct.product_weight) {
          let productWeight = product.campaignProduct.product_weight;
          //convert weight into weight in grams
          if (product.campaignProduct.product_unit == "Ounce") {
            productWeight *= 28.35;
          }
          if (product.campaignProduct.product_unit == "Pound") {
            productWeight *= 454;
          }
          weight += productWeight * product.quantity;
        } else {
          //if product has no weight associated with it (for some reason) add 454 grams per item
          weight += 454 * product.quantity;
        }
      });
      return weight;
    },
  },

  watch: {
    loading: {
      handler() {
        this.$store.commit("setCartLoading", this.loading);
      },
    },

    cart: {
      handler() {
        this.formattedCart = JSON.parse(JSON.stringify(this.cart));
        this.calculateSubTotal();

        this.commitCartValid();

        // PR-N1: refresh the resolver Map for the current cart
        // shape. Fire-and-forget; when it resolves the assignment
        // triggers a reactive re-render that picks up the now-known
        // decoration labels. ``buildCartDecorations`` defaults to
        // "no label" while the Map is empty so the cart render
        // never blocks on the resolver.
        this.refreshDecorationResolution();
      },
    },

    pricesReady() {
      this.commitCartValid();
      this.calculateSubTotal();
    },

    quoteInputs() {
      this.scheduleQuote();
    },

    // Re-price whenever the store's cart changes (quantity step, delete,
    // an add from the cart builder).
    "$store.state.cart": {
      deep: true,
      handler() {
        this.refreshLinePrices();
      },
    },

  },

  created() {
    window.addEventListener("resize", this.handleWindowResize);
  },

  unmounted() {
    window.removeEventListener("resize", this.handleWindowResize);
    clearTimeout(this.quoteTimer);
    // A pass still in flight must not write into the next order dialog.
    this.quoteSeq += 1;
    // Do not leave a stale "no rate" flag, quote or destination for the
    // next order dialog.
    this.$store.commit("setShopperShippingAvailable", true);
    this.$store.commit("setShopperShippingQuoteId", null);
    this.$store.commit("setShopperShipTo", null);
    this.$store.commit("setShopperShipToState", "idle");
  },

  async mounted() {
    this.loading = true;
    this.hideComponentInfo = true;

    await this.loadProducts();
    await this.refreshLinePrices();

    this.loading = false;
    this.hideComponentInfo = false;
  },

  methods: {
    // PR-N1: Resolve (decoration_id, color_sku) pairs for the current
    // cart shape via POST /api/decorations/resolve_batch. The
    // resulting Map is stored on ``this.decorationResolution`` and
    // consumed synchronously by the ``cart`` computed via
    // ``buildCartDecorations``. Resolver failures fall back to empty
    // (no label) -- same UX as the pre-PR-N1 silent miss for the
    // failure case, but on the right axis.
    //
    // Re-resolves ONLY when the pair set changes. This runs from the `cart`
    // watcher, and assigning `decorationResolution` recomputes `cart` (it
    // reads the Map), which re-fires that watcher: without this guard it
    // looped for as long as the page was open -- 72 resolve_batch POSTs in
    // 400ms measured with one decorated line, and an out-of-memory spin
    // with an undecorated one (2026-09-29).
    async refreshDecorationResolution() {
      const pairs = collectResolverPairs(this.formattedCart);
      const key = pairs
        .map((p) => `${p.placement_id}|${p.color_sku}`)
        .sort()
        .join(",");
      if (key === this.decorationPairsKey) return;
      this.decorationPairsKey = key;
      const { resolveBatch } = useResolveBatch();
      this.decorationResolution = await resolveBatch(pairs);
    },

    // Fetch the server's per-line prices for the account's cart. Failure
    // leaves every line unpriced, which shows the error with Retry and
    // blocks the order -- never a locally computed price.
    //
    // Sequenced: a quantity step while a request is in flight starts a
    // second one, and the FIRST can answer last. Only the newest request
    // may write, so a stale response (or a stale failure) cannot replace
    // the prices of the cart as it is now (#3884 gate P2-4).
    async refreshLinePrices() {
      const seq = (this.linePricesSeq || 0) + 1;
      this.linePricesSeq = seq;
      this.linePricesState = "loading";
      this.linePricesError = null;
      try {
        const prices = await fetchCartLinePrices(
          this.$axios,
          this.$store.state.account,
          this.storeID
        );
        if (seq !== this.linePricesSeq) return;
        this.linePrices = prices;
        this.linePricesState = "loaded";
      } catch (err) {
        if (seq !== this.linePricesSeq) return;
        console.warn("Order cart: could not load prices.", describeError(err));
        this.linePrices = null;
        this.linePricesError = cartPricesErrorMessage(err);
        this.linePricesState = "error";
      }
    },

    commitCartValid() {
      this.$store.commit(
        "setCartValid",
        this.formattedCart.length > 0 && this.pricesReady
      );
    },
    // The items figure shown while the server has not priced the order. It
    // is a DISPLAY figure only: the order's money is `serverQuote.totals`.
    calculateSubTotal() {
      if (!this.pricesReady) {
        this.subTotal = 0;
        return;
      }
      this.subTotal = this.getSubTotal(
        this.formattedCart,
        this.campaign.fundraising_type,
        this.campaign.fundraising_amount_per_item,
        this.fundraisingOn
      );
    },

    scheduleQuote() {
      clearTimeout(this.quoteTimer);
      // The inputs changed: whatever was priced no longer describes them,
      // and the rate's id must not be placed against them.
      this.quoteSeq += 1;
      this.serverQuote = null;
      this.shippingQuoteId = null;
      this.$store.commit("setShopperShippingQuoteId", null);
      this.quoteTimer = setTimeout(() => this.refreshQuote(), QUOTE_DEBOUNCE_MS);
    },

    /**
     * Price the order the way the storefront does (#540, Jake 2026-10-26):
     *
     *  1. Where it ships. A company / department
     *     store ships where the SERVER says (`checkout_quote.ship_to`, shown
     *     by CartShipToDestination); nobody types it. Any other store ships
     *     to the address typed into Shopper Details.
     *  2. A live rate for that destination (the store's shipping origin is
     *     the server's). A flat store has none: the server prices its
     *     flat_rate / flat_rate_range / cart_percentage config itself
     *     (Bug Intake #549); this cart computes no shipping.
     *  3. `checkout_quote` with the rate's id: tax, shipping, processing and
     *     service fee, allowance and total, computed by the functions
     *     checkout recomputes with. A flat config that cannot price the
     *     cart answers `shipping_unpriced`: "rate unavailable", no order.
     *
     * Sequenced: only the newest pass writes. Each stop says why the order
     * is not priced yet (quoteState), and without a quote id a live-rate
     * order cannot be placed (AddOrder's gate; the server 409s too).
     */
    async refreshQuote() {
      const seq = this.quoteSeq + 1;
      this.quoteSeq = seq;
      const isCurrent = () => seq === this.quoteSeq;
      const campaign = this.campaign;
      this.serverQuote = null;
      this.shippingQuoteId = null;
      this.setShippingRateUnavailable(false);
      this.$store.commit("setShopperShippingQuoteId", null);
      if (!campaign || !this.pricesReady || !this.formattedCart.length) {
        this.quoteState = "idle";
        return;
      }

      const departmentId = this.clientDepartmentId ?? null;
      const accountId = this.$store.state.account._jv.id;
      const resolves = storeResolvesDestination(campaign);
      let destination = null;
      let buyerLocation = null;

      // The Location label (a pick-up location included, #548) plays no part:
      // the store's shipping mode alone decides where the order ships.
      if (resolves) {
        if (
          departmentPickerMode(campaign.client_departments) === "required" &&
          departmentId === null
        ) {
          this.$store.commit("setShopperShipTo", null);
          this.$store.commit("setShopperShipToState", "awaiting-department");
          this.quoteState = "awaiting-department";
          return;
        }
        this.$store.commit("setShopperShipToState", "loading");
        this.quoteState = "loading";
        let answer;
        try {
          answer = await requestCheckoutQuote(
            this.$axios,
            accountId,
            this.storeID,
            { client_department_id: departmentId },
            { [SUPPRESS_ERROR_TOAST]: true }
          );
        } catch (err) {
          if (!isCurrent()) return;
          console.warn("Order cart: no destination.", describeError(err));
          this.$store.commit("setShopperShipTo", null);
          this.$store.commit("setShopperShipToState", "failed");
          this.quoteState = "failed";
          return;
        }
        if (!isCurrent()) return;
        const shipTo = (answer && answer.ship_to) || null;
        this.$store.commit("setShopperShipTo", shipTo);
        this.$store.commit("setShopperShipToState", "loaded");
        destination = shipToQuoteLocation(shipTo);
      } else {
        destination = this.typedAddress();
        buyerLocation = destination;
      }

      // A live-rate store needs its rate's id; a FLAT store (flat_rate,
      // flat_rate_range, cart_percentage) is priced by the server from its
      // own config (Bug Intake #549), so nothing is computed here.
      if (campaign.shipping_cost_enabled && storeRatesLive(campaign)) {
        if (!destination) {
          this.quoteState = resolves ? "no-destination" : "needs-address";
          return;
        }
        this.quoteState = "loading";
        const rate = await this.liveRate(destination, isCurrent);
        if (!isCurrent() || !rate) return;
        this.shippingQuoteId = rate.quoteId;
      } else if (!resolves && !destination) {
        // The address still decides the tax.
        this.quoteState = "needs-address";
        return;
      }

      this.quoteState = "loading";
      let result;
      try {
        result = await requestCheckoutQuote(
          this.$axios,
          accountId,
          this.storeID,
          {
            // A company / department store's destination is the server's
            // own.
            buyer_location: resolves ? null : buyerLocation,
            // No shipping figure: the server prices it (the stored live
            // quote this id names, or the store's flat config).
            shipping_quote_id: this.shippingQuoteId,
            coupon_code: this.getShopperCoupon || null,
            client_department_id: departmentId,
          },
          { [SUPPRESS_ERROR_TOAST]: true }
        );
      } catch (err) {
        if (!isCurrent()) return;
        console.warn("Order cart: could not price the order.", describeError(err));
        this.quoteState = "failed";
        return;
      }
      if (!isCurrent()) return;
      if (result && result.shipping_unpriced) {
        // The store's flat shipping config cannot price this cart: the
        // rate is unavailable and the order cannot be placed (#549).
        this.setShippingRateUnavailable(true);
        this.quoteState = "failed";
        return;
      }
      if (!result || result.shipping_quote_required || !result.totals) {
        this.quoteState = "failed";
        return;
      }
      this.serverQuote = result;
      this.quoteState = "loaded";
      // The SERVER's shipping line (live quote or flat config).
      this.shippingCost = Number(result.shipping_cost) || 0;
      this.$store.commit("setShopperShipping", this.shippingCost);
      this.$store.commit("setShopperTax", result.sales_tax);
      this.$store.commit("setShopperTaxInfo", result.tax_info || {});
      this.$store.commit("setShopperTotal", result.totals.total);
      // Last: the id is what lets the order be placed (AddOrder's gate).
      this.$store.commit("setShopperShippingQuoteId", this.shippingQuoteId);
    },

    // The address typed into Shopper Details, once it is complete enough to
    // rate (street, city, postal code, state and country), else null. Not
    // AVS-validated yet: Place Order validates it, and a corrected address
    // is a new input that is priced again.
    typedAddress() {
      const location = this.getShopperLocation;
      if (!location || typeof location !== "object") return null;
      const filled = (key) =>
        typeof location[key] === "string" && location[key].trim() !== "";
      if (
        !["street_and_number", "city", "zip_code", "region", "country"].every(
          filled
        )
      ) {
        return null;
      }
      return this.formatLocation(location, false);
    },

    getPrice(item) {
      return this.getItemPrice(
        item,
        this.campaign.fundraising_type,
        this.campaign.fundraising_amount_per_item,
        this.fundraisingOn
      );
    },

    // The single place the "no rate" state is set, so the cart's own
    // display and the store flag AddOrder gates on can never disagree.
    setShippingRateUnavailable(unavailable) {
      this.shippingRateUnavailable = unavailable;
      this.$store.commit("setShopperShippingAvailable", !unavailable);
    },

    /**
     * A live rate for `destination`: `{cost, quoteId}`, or null after
     * marking the rate unavailable (never a $0 rate, #2882). The ship-from
     * is the SERVER's (client company -> distributor -> decorator); the
     * request names the store, never an origin. Asked ONCE per destination /
     * weight / service (Jake, 2026-10-22; checkoutQuoteMemo.js), shared
     * with the storefront cart.
     */
    async liveRate(destination, isCurrent) {
      const fail = () => {
        if (isCurrent()) this.setShippingRateUnavailable(true);
        return null;
      };
      const request = liveShippingRequest(this.campaign, {
        location: destination,
        totalWeight: this.cartTotalWeight,
        userId: this.$store.state.account._jv.id,
        source: "add-order-dialog",
      });
      const fetchQuote = async () => {
        const answer = await this.$axios.post(
          "/shipping/getSmartShippingCost",
          request
        );
        logShippingRateProvider(answer, "add-order-dialog");
        return answer;
      };
      const quoteKey = liveShippingQuoteKey(
        this.campaign,
        destination,
        request.totalWeight
      );

      let response;
      try {
        response = quoteKey
          ? await quoteMemoFor(this, "shipping").run(quoteKey, fetchQuote, {
              cacheable: isUsableShippingQuote,
            })
          : await fetchQuote();
      } catch (err) {
        if (!isCurrent()) return null;
        // The request itself threw: there is no response to read.
        console.log(describeError(err));
        this.$store.dispatch("raiseError", err);
        return fail();
      }
      if (!isCurrent()) return null;
      const data = response && response.data;
      if (data && data.error) {
        this.$store.dispatch(
          "raiseError",
          isNoShipOriginResponse(response)
            ? NO_SHIP_ORIGIN_STAFF_MESSAGE
            : data.error
        );
        return fail();
      }
      // Strict: parseFloat("12abc") is 12, Number("0x10") is 16, and both
      // would be charged. Only a number or a plain decimal string is a rate.
      const quoted =
        typeof data === "number"
          ? data
          : typeof data === "string" && /^\s*\d+(\.\d+)?\s*$/.test(data)
          ? Number(data)
          : NaN;
      if (!Number.isFinite(quoted) || quoted < 0) {
        return fail();
      }
      // The quote is the TOTAL the buyer pays (carrier rate plus
      // ThreadFlow's surcharge, stored by the server under the quote id). A
      // genuine $0 quote is a RATE; the error envelope means "no rate".
      return { cost: quoted, quoteId: readShippingQuoteId(response) };
    },

    async getParsedAddress(address) {
      let response;
      try {
        response = await this.$axios.post(
          "/address_validation/getParsedAddress",
          {
            location: address,
          }
        );
      } catch (err) {
        console.log(describeError(err));
        this.$store.dispatch("raiseError", err);
        return "error";
      } finally {
      }
      if (isUnparseableAddress(response.data)) {
        this.$store.dispatch(
          "raiseError",
          unparseableAddressMessage(response.data)
        );
        return "error";
      }
      return response.data;
    },

    formatLocation(location, isCompanyLocation = true) {
      if (location) {
        let formattedLocation = {};
        formattedLocation["street_and_number"] = location["street_and_number"];
        formattedLocation["city"] = location["city"];
        formattedLocation["zip_code"] = location["zip_code"];
        formattedLocation["region"] = location["region"];
        formattedLocation["country"] = location["country"];
        formattedLocation["suite_unit_etc"] = location["suite_unit_etc"]
          ? location["suite_unit_etc"]
          : "";
        formattedLocation["isCompanyLocation"] = isCompanyLocation;
        return formattedLocation;
      }
    },
    formatPrice(number) {
      return number
        ? this.numberWithCommas(parseFloat(number).toFixed(2))
        : "0.00";
    },
    // A breakdown line's amount; a credit (allowance, coupon) is negative.
    formatAmount(amount) {
      const value = Number(amount) || 0;
      const text = `$${this.formatPrice(Math.abs(value))}`;
      return value < 0 ? `-${text}` : text;
    },
    handleWindowResize(e) {
      let width = window.innerWidth;
      if (width < 960) {
        this.mobile = true;
      } else {
        this.mobile = false;
      }
    },
    async loadProducts() {
      let result;
      try {
        result = await this.$axios.get(
          `/product/getCampaignProductList/${this.storeID}`
        );
      } catch (err) {
        console.log(describeError(err));
        this.$store.dispatch("raiseError", err);
      } finally {
        this.products = result.data;
      }
    },

    async updateQuantity(index, operation) {
      let item = this.cart[index];
      if (operation == "increment") {
        item.quantity = item.quantity + 1;
        this.formattedCart.splice(index, 1, item);
      } else if (operation == "decrement") {
        item.quantity = item.quantity - 1;
        this.formattedCart.splice(index, 1, item);
      } else {
        item.quantity = 0;
        this.formattedCart.splice(index, 1);
      }
      let response;
      try {
        response = await this.$axios.post(
          `/accounts/${this.$store.state.account._jv.id}/update_cart_quantity`,
          {
            meta: {
              method: "update_cart_quantity",
              args: {
                ...cartStoreArgs(this.storeID),
                campaign_product_id: item.campaignProduct.id,
                primary_attribute_value: item.primaryAttributeValue,
                secondary_attribute_value: item.secondaryAttributeValue,
                // The RPC treats an ABSENT logo as a wildcard matching the
                // first same-size line, so without it +/- and delete on a
                // second logo's line acted on the first. Omitted for an
                // undecorated line, which keeps its old payload.
                ...(item.selectedArtworkId != null
                  ? { selected_artwork_id: item.selectedArtworkId }
                  : {}),
                quantity: item.quantity,
              },
            },
          }
        );
        if (response.data.meta.result.message) {
          if (this.displayCartLimitError) {
            this.$store.dispatch(
              "raiseError",
              response.data.meta.result.message
            );
            this.displayCartLimitError = false;
          }
        } else {
          this.displayCartLimitError = true;
        }
      } catch (err) {
        console.log(describeError(err));
        this.$store.dispatch("raiseError", err);
      } finally {
        if (response) {
          this.$store.commit("setCart", response.data.meta.result.cart);
        }
      }
    },

    increaseQuantity(index) {
      this.updateQuantity(index, "increment");
    },

    decreaseQuantity(index) {
      this.updateQuantity(index, "decrement");
    },

    deleteOrderItem(index) {
      this.updateQuantity(index, "delete");
    },

    async loadCartProduct(id) {
      try {
        //if product not in vuex store, add it to store
        let includeList =
          "product,product.product_type,product.product_type.primary_attribute,product.product_type.product_name," +
          "product.product_type.secondary_attribute,supplier";

        await this.$store.dispatch("jv/get", [
          `campaign_products/${id}`,
          { params: { include: includeList } },
        ]);
      } catch (err) {
        console.log(describeError(err));
        this.$store.dispatch("raiseError", err);
      }

      //this.products = Object.values(campaign.campaign_products);
    },

    // `images` is NULL for a product with no pictures; undefined here makes
    // the template's getImage() fall back to the placeholder. Reading it
    // unguarded threw during render and left a stale cart on screen (#488).
    getImageID(item) {
      let productID = item.campaignProduct.id;
      let imageID;
      this.products.forEach((product) => {
        const id = product._jv ? product._jv.id : product.id;
        if (id == productID) {
          imageID = (product.images || {})[item.primaryAttributeValue];
        }
      });
      return imageID;
    },
  },
};
</script>

<style scoped>
.container-card {
  height: 100%;
  width: 100%;
  border-radius: 5px;
}

.order-item-card {
  width: 100%;
  padding-top: 16px;
}

.quantity {
  width: 25px;
}

.top-bar {
  background: white;
}
</style>

